import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import './ServiceReminders.css';

function ServiceReminders({ onLogout }) {
  const navigate = useNavigate();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [message, setMessage] = useState('');

  const token = localStorage.getItem('token');
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const loadReminders = useCallback(async () => {
    setLoading(true);
    setMessage('');

    try {
      const response = await axios.get('/api/service-reminders', auth);
      setReminders(response.data.reminders || []);
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to load service reminders.');
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => {
    loadReminders();
  }, [loadReminders]);

  const generateNow = async () => {
    setGenerating(true);
    setMessage('');

    try {
      const response = await axios.post('/api/service-reminders/generate', {}, auth);
      setMessage(`${response.data.insertedCount || 0} new reminder(s) generated.`);
      await loadReminders();
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to generate reminders.');
    } finally {
      setGenerating(false);
    }
  };

  const deleteReminder = async (reminderId) => {
    setDeletingId(reminderId);
    setMessage('');

    try {
      await axios.delete(`/api/service-reminders/${reminderId}`, auth);
      setReminders((current) => current.filter((item) => item.ServiceReminderId !== reminderId));
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to delete reminder.');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateValue) => {
    if (!dateValue) {
      return '-';
    }

    return new Date(dateValue).toLocaleDateString();
  };

  return (
    <div className="home-container">
      <Sidebar onLogout={onLogout} />
      <main className="service-reminders-page">
        <header className="service-reminders-header app-page-header">
          <div className="app-title-group">
            <button className="app-back-button" type="button" onClick={() => navigate('/')} aria-label="Back to home">
              Back
            </button>
            <div className="app-title-copy">
              <h1>Service Reminders</h1>
              <p>Automatic six-month reminder list with the next recommended odometer target.</p>
            </div>
          </div>
          <div className="app-header-actions">
            <button type="button" onClick={loadReminders} disabled={loading}>
              Refresh
            </button>
            <button type="button" onClick={generateNow} disabled={generating}>
              {generating ? 'Generating...' : 'Generate Now'}
            </button>
          </div>
        </header>

        <section className="reminder-summary-strip">
          <div>
            <span>Total reminders</span>
            <strong>{reminders.length}</strong>
          </div>
          <div>
            <span>Email ready</span>
            <strong>{reminders.filter((item) => item.ClientEmail).length}</strong>
          </div>
          <div>
            <span>SMS ready</span>
            <strong>{reminders.filter((item) => item.ClientPhone).length}</strong>
          </div>
        </section>

        {message && <div className="service-reminders-message">{message}</div>}

        <section className="service-reminders-card">
          {loading ? (
            <div className="service-reminders-empty">Loading service reminders...</div>
          ) : reminders.length === 0 ? (
            <div className="service-reminders-empty">
              No service reminders are due. New reminders are checked daily at 12:00 PM.
            </div>
          ) : (
            <div className="service-reminders-list">
              {reminders.map((reminder) => (
                <article className="service-reminder-item" key={reminder.ServiceReminderId}>
                  <div className="reminder-type">
                    <span>{reminder.ReminderType}</span>
                    <strong>{formatDate(reminder.DueDate)}</strong>
                  </div>

                  <div className="reminder-main">
                    <h2>{reminder.ClientName || 'Unknown client'}</h2>
                    <p>{reminder.Message}</p>
                    <div className="reminder-meta">
                      <span>{reminder.CarNumber || reminder.VehicleText || 'Vehicle not set'}</span>
                      <span>Last service: {formatDate(reminder.LastServiceDate)}</span>
                      <span>{reminder.LastServiceOdometerKm ? `Last: ${reminder.LastServiceOdometerKm} KM` : 'KM not set'}</span>
                      <span>{reminder.DueOdometerKm ? `Target: ${reminder.DueOdometerKm} KM` : 'Target KM not set'}</span>
                    </div>
                    <div className="reminder-contact">
                      <span>Email: {reminder.ClientEmail || '-'}</span>
                      <span>SMS: {reminder.ClientPhone || '-'}</span>
                    </div>
                  </div>

                  <div className="reminder-actions">
                    <button type="button" onClick={() => navigate(`/jobcards/${reminder.SourceJobCardId}/edit`)}>
                      Job Card
                    </button>
                    <button
                      type="button"
                      className="delete"
                      onClick={() => deleteReminder(reminder.ServiceReminderId)}
                      disabled={deletingId === reminder.ServiceReminderId}
                    >
                      {deletingId === reminder.ServiceReminderId ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

export default ServiceReminders;
